/**
 * CrewAssignmentsTab — single tab that hosts the Crew Assignment workflow
 * across every field role:
 *   • Supervisors get the Composer wizard PLUS the list of assignments they created.
 *   • Crew Leaders see only the assignments dispatched to them + can organize.
 *   • Site Agents see the trimmed job cards for their portion of each run.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { RefreshCw, Inbox } from 'lucide-react';
import { Button } from '../ui/button';
import CrewAssignmentComposer from '../CrewAssignmentComposer';
import CrewAssignmentCard from '../CrewAssignmentCard';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CrewAssignmentsTab({ user, regions = [], properties = [] }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamStaff, setTeamStaff] = useState([]);

  const isSupervisor = ['supervisor', 'manager', 'admin', 'company_manager'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/crew-assignments/mine`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAssignments(data.assignments || []);
      }
      if (isSupervisor) {
        const tv = await fetch(`${API}/api/routes/team-view`, { credentials: 'include' });
        if (tv.ok) {
          const tvd = await tv.json();
          const entry = tvd.find((e) => e.region?.region_id !== '_unassigned') || tvd[0];
          setTeamStaff(entry?.staff || []);
        }
      }
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [isSupervisor]);

  useEffect(() => { load(); }, [load]);

  const region = regions[0] || null;
  const crewLeaders = useMemo(
    () => teamStaff.filter((s) => s.role === 'crew_leader'),
    [teamStaff],
  );
  const agents = useMemo(
    () => teamStaff.filter((s) => ['agent', 'site_agent', 'staff', 'driver'].includes(s.role)),
    [teamStaff],
  );

  return (
    <div className="space-y-4" data-testid="crew-assignments-tab-content">
      {isSupervisor && (
        <CrewAssignmentComposer
          region={region}
          crewLeaders={crewLeaders}
          agents={agents}
          properties={properties}
          onCreated={load}
        />
      )}

      <div className="flex items-center justify-between">
        <h4
          className="text-sm font-bold uppercase tracking-widest"
          style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {isSupervisor ? 'Dispatched Assignments' : 'Your Assignments'}
        </h4>
        <Button
          size="sm"
          onClick={load}
          variant="outline"
          className="border-zinc-600 text-zinc-300 h-7 text-xs"
          data-testid="crew-assignments-refresh"
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {assignments.length === 0 ? (
        <Card className="bg-zinc-900 border border-[#C9A962]/40 p-6 text-center">
          <Inbox className="h-8 w-8 mx-auto text-zinc-600 mb-2" />
          <p className="text-white font-semibold">
            {isSupervisor ? 'No crew assignments dispatched yet.' : 'No crew assignments waiting for you.'}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {isSupervisor
              ? 'Use the composer above to dispatch your first job card to a Crew Leader.'
              : 'Your supervisor will dispatch new job cards here in real time.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="crew-assignments-list">
          {assignments.map((a) => (
            <CrewAssignmentCard
              key={a.assignment_id}
              assignment={a}
              viewerRole={user?.role}
              viewerUserId={user?.user_id}
              onChanged={(updated) => {
                setAssignments((prev) => prev.map((x) => (x.assignment_id === updated.assignment_id ? updated : x)));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
